/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import Loader from 'src/common/Loader';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { useDispatch } from 'react-redux';
import { getAllSites } from 'src/redux/siteSlice';
import { getAllTanks } from 'src/redux/tankSlice';
import {
  addNewDispenser,
  updateDispenserDetails
} from 'src/redux/dispenserSlice';
import FMSModal from 'src/common/FMSModal';
import DispenserDetailsForm from './forms/DispenserDetailsForm';
import CounterConfigForm from './forms/CounterConfigForm';
import DispenserConfigForm from './forms/DispenserConfigForm';
import validationSchema from './formModel/dispenserValidationSchema';
import dispenserFormModel from './formModel/dispenserFormModel';
import formInitialValues from './formModel/dispenserInitialValues';
import MeterConfigForm from './forms/MeterConfigForm';
import TimerConfigForm from './forms/TimerConfigForm';
import AdditionalConfigForm from './forms/AdditionalConfigForm';

const { formId, formField } = dispenserFormModel;

function renderStepContent(step, initialValues, extraProps = {}) {
  switch (step) {
    case 0:
      return <DispenserDetailsForm formField={formField} initValues={!!initialValues} {...extraProps} />;
    case 1:
      return <DispenserConfigForm formField={formField} {...extraProps} />;
    case 2:
      return <CounterConfigForm formField={formField} {...extraProps} />;
    case 3:
      return <MeterConfigForm formField={formField} {...extraProps} />;
    case 4:
      return <TimerConfigForm formField={formField} {...extraProps} />;
    case 5:
      return <AdditionalConfigForm formField={formField} {...extraProps} />;
    default:
      return <div>Not Found</div>;
  }
}

const NewDispenser = ({ open, handleClose, initialValues }) => {
  const dispatch = useDispatch();

  const steps = [
    'Dispenser Details',
    'Dispenser Configuration',
    'Counter Configuration',
    'Meter Configuration',
    'Timer Configuration',
    'Additional Configuration'
  ];

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const formInitValues = initialValues || formInitialValues;
  const isEditMode = Boolean(initialValues);
  const requiredHintText = '';

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
      dispatch(getAllTanks());
      setActiveStep(0);
    }
  }, [open, dispatch]);

  const handleBack = () => {
    if (activeStep === 0) {
      handleClose();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const submitForm = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateDispenserDetails(values)).unwrap();
      } else {
        await dispatch(addNewDispenser(values)).unwrap();
      }

      handleClose();

      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Dispenser updated successfully!'
            : 'New Dispenser created successfully!'
        })
      );
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(
        setErrorInfo({
          errorMsg: error?.error_message || 'Something went wrong'
        })
      );
    }
    setLoading(false);
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep((prev) => prev + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const getActionButton = () => {
    if (!isLastStep) return 'Next';
    if (initialValues) return 'Update Dispenser';
    return 'Create Dispenser';
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography fontWeight={700} fontSize={18}>
          {initialValues ? 'UPDATE DISPENSER' : 'ADD NEW DISPENSER'}
        </Typography>
      </Box>

      <DialogContent
        sx={{
          px: 3,
          py: 0,
          overflowX: 'hidden'
        }}
      >
        {!loading && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 5,
              mx: -3,
              px: 3,
              pt: 1.5,
              pb: 1,
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E5E7EB'
            }}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                maxWidth: 760,
                mx: 'auto',
                '& .MuiStep-root': {
                  px: { xs: 0.5, sm: 1 }
                },
                '& .MuiStepLabel-labelContainer': {
                  mt: 0.5
                },
                '& .MuiStepLabel-label': {
                  fontSize: 13,
                  fontWeight: 500
                },
                '& .MuiStepLabel-label.Mui-active': {
                  fontWeight: 700,
                  color: 'primary.main'
                }
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        <Formik
          initialValues={formInitValues}
          validationSchema={currentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => {
            if (loading) {
              return <Loader />;
            }

            return (
              <Form id={formId}>
                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    border: '1px solid #E5E7EB',
                    borderRadius: 2,
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  {!isEditMode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      * Required fields
                    </Typography>
                  )}
                  {renderStepContent(activeStep, initialValues, { requiredHintText })}
                </Box>

                <DialogActions
                  sx={{
                    mt: 3,
                    mx: -3,
                    px: 3,
                    py: 2,
                    justifyContent: 'flex-end',
                    gap: 2,
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 5,
                    backgroundColor: '#FFFFFF',
                    borderTop: '1px solid #E5E7EB'
                  }}
                >
                  {activeStep === 0 ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleClose}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Close
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Back
                    </Button>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    {getActionButton()}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </FMSModal>
  );
};

NewDispenser.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewDispenser;
